import { useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Paper,
  FormGroup,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Divider,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useResetCurrentUser } from "../../hooks/useResetCurrentUser";
import { EDUCATOR_EVENT_TYPES, useReporting } from "../../hooks/useReporting";

const APPCLIENT_ID = import.meta.env.VITE_APP_APPCLIENT_ID;
const BASE_AUTH_URL = import.meta.env.VITE_APP_BASE_AUTH_URL;

const USER_FIELD_OPTIONS = [
  { key: "facets", label: "Facets" },
  { key: "ladderProgress", label: "Ladder Progress" },
  { key: "xp", label: "XP" },
  { key: "nextSteps", label: "Next Steps" },
  { key: "profileChoices", label: "Profile Choices" },
];

const CHILD_RECORD_OPTIONS = [
  { key: "cc", label: "Career Climbs" },
  { key: "careerplan", label: "Career Plans" },
  { key: "courseplan", label: "Course Plans" },
  { key: "act", label: "Activity Completions" },
  { key: "award", label: "Award Completions" },
  { key: "course", label: "Course Completions" },
  { key: "event", label: "Events" },
  { key: "cluster", label: "Cluster Content" },
  { key: "pathway", label: "Pathway Content" },
  { key: "bba", label: "Building Block Activities" },
  { key: "hat", label: "Hat Statuses & Sentiments" },
  { key: "connector", label: "Connector Sentiments" },
  { key: "badge", label: "Badges" },
  { key: "shoutout", label: "Shout Outs Received" },
];

const allUserFieldKeys = USER_FIELD_OPTIONS.map((o) => o.key);
const allChildRecordKeys = CHILD_RECORD_OPTIONS.map((o) => o.key);

const makeDefaultOptions = () => ({
  ...Object.fromEntries(allUserFieldKeys.map((k) => [k, true])),
  childRecords: Object.fromEntries(allChildRecordKeys.map((k) => [k, true])),
});

export const ResetUserPage = () => {
  const [confirmationText, setConfirmationText] = useState("");
  // "all" is the common case by a distance, so it's the landing mode.
  const [mode, setMode] = useState("all");
  const [options, setOptions] = useState(makeDefaultOptions);
  const [preserveEducatorEvents, setPreserveEducatorEvents] = useState(false);
  const [logOut, setLogOut] = useState(true);
  const [success, setSuccess] = useState(false);
  const { resetUser, isLoading, error } = useResetCurrentUser();
  const { reportClimberReset } = useReporting();

  const CONFIRMATION_PHRASE = "reset me";

  const allUserFieldsChecked = allUserFieldKeys.every((k) => options[k]);
  const someUserFieldsChecked = allUserFieldKeys.some((k) => options[k]);
  const allChildRecordsChecked = allChildRecordKeys.every(
    (k) => options.childRecords[k],
  );
  const someChildRecordsChecked = allChildRecordKeys.some(
    (k) => options.childRecords[k],
  );
  const allChecked = allUserFieldsChecked && allChildRecordsChecked;
  const someChecked = someUserFieldsChecked || someChildRecordsChecked;

  const handleToggleUserField = (key) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleToggleChildRecord = (key) => {
    setOptions((prev) => ({
      ...prev,
      childRecords: {
        ...prev.childRecords,
        [key]: !prev.childRecords[key],
      },
    }));
  };

  const handleToggleAllUserFields = () => {
    const newVal = !allUserFieldsChecked;
    setOptions((prev) => ({
      ...prev,
      ...Object.fromEntries(allUserFieldKeys.map((k) => [k, newVal])),
    }));
  };

  const handleToggleAllChildRecords = () => {
    const newVal = !allChildRecordsChecked;
    setOptions((prev) => ({
      ...prev,
      childRecords: Object.fromEntries(
        allChildRecordKeys.map((k) => [k, newVal]),
      ),
    }));
  };

  const handleToggleAll = () => {
    const newVal = !allChecked;
    setOptions({
      ...Object.fromEntries(allUserFieldKeys.map((k) => [k, newVal])),
      childRecords: Object.fromEntries(
        allChildRecordKeys.map((k) => [k, newVal]),
      ),
    });
  };

  const resetEverything = mode === "all";
  const canSubmit =
    confirmationText === CONFIRMATION_PHRASE &&
    !isLoading &&
    (resetEverything || someChecked);

  const handleReset = async () => {
    if (!canSubmit) return;

    setSuccess(false);
    try {
      await reportClimberReset();
      await resetUser({
        confirmation: CONFIRMATION_PHRASE,
        // full: true rather than every box ticked — the server expands it, so
        // this can't silently miss a domain the page doesn't know about yet.
        options: resetEverything ? { full: true } : options,
        preserveEventTypes:
          !resetEverything && preserveEducatorEvents
            ? [...EDUCATOR_EVENT_TYPES]
            : undefined,
      });
      if (logOut) {
        sessionStorage.clear();
        localStorage.clear();
        const logoutUrl = `${BASE_AUTH_URL}/logout?client_id=${APPCLIENT_ID}&logout_uri=${encodeURI(
          window.location.origin,
        )}/`;
        window.location.href = logoutUrl;
      } else {
        setSuccess(true);
      }
    } catch {
      // onError in the hook will log the error
    }
  };

  return (
    <Paper
      sx={{ p: 3, maxWidth: 800, margin: "auto", mt: 4 }}
      label="ResetUserContainer"
    >
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        color="error"
        label="ResetUserTitle"
      >
        Reset User
      </Typography>
      <Typography variant="body1" paragraph label="ResetUserWarning">
        <strong>Warning:</strong> Data will be permanently deleted for the
        currently logged-in user. This cannot be undone.
      </Typography>

      <FormControl sx={{ mb: 2 }} label="ResetModeControl">
        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          name="reset-mode"
        >
          <FormControlLabel
            value="all"
            control={<Radio disabled={isLoading} />}
            label="Reset everything"
          />
          <FormControlLabel
            value="selected"
            control={<Radio disabled={isLoading} />}
            label="Reset only selected items"
          />
        </RadioGroup>
      </FormControl>

      {resetEverything && (
        <Alert severity="warning" sx={{ mb: 2 }} label="ResetEverythingSummary">
          Wipes all progress, profile and activity data, plus every event record
          &mdash; including educator ones such as logins and resource downloads.
          Pick &ldquo;Reset only selected items&rdquo; to keep those.
        </Alert>
      )}

      <FormGroup label="ResetOptionsGroup">
        {!resetEverything && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked && !allChecked}
                  onChange={handleToggleAll}
                  disabled={isLoading}
                />
              }
              label={<strong>Select All</strong>}
              sx={{ mb: 1 }}
            />
            <Divider sx={{ mb: 1 }} />

            <Box
              sx={{ display: "flex", gap: 4, mt: 1 }}
              label="ResetColumnsContainer"
            >
              <Box sx={{ flex: 1 }} label="UserFieldColumn">
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Progress &amp; Profile
                </Typography>
                <Box sx={{ ml: 2 }} label="UserFieldOptions">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allUserFieldsChecked}
                        indeterminate={
                          someUserFieldsChecked && !allUserFieldsChecked
                        }
                        onChange={handleToggleAllUserFields}
                        disabled={isLoading}
                        size="small"
                      />
                    }
                    label={<em>All progress &amp; profile</em>}
                  />
                  {USER_FIELD_OPTIONS.map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={options[key]}
                          onChange={() => handleToggleUserField(key)}
                          disabled={isLoading}
                          size="small"
                        />
                      }
                      label={label}
                      sx={{ display: "block" }}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ flex: 1 }} label="ChildRecordColumn">
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Activity &amp; Content Records
                </Typography>
                <Box sx={{ ml: 2 }} label="ChildRecordOptions">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allChildRecordsChecked}
                        indeterminate={
                          someChildRecordsChecked && !allChildRecordsChecked
                        }
                        onChange={handleToggleAllChildRecords}
                        disabled={isLoading}
                        size="small"
                      />
                    }
                    label={<em>All activity &amp; content</em>}
                  />
                  {CHILD_RECORD_OPTIONS.map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={options.childRecords[key]}
                          onChange={() => handleToggleChildRecord(key)}
                          disabled={isLoading}
                          size="small"
                        />
                      }
                      label={label}
                      sx={{ display: "block" }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={preserveEducatorEvents}
                  onChange={() => setPreserveEducatorEvents((prev) => !prev)}
                  disabled={isLoading}
                  size="small"
                />
              }
              label="Keep educator events (logins, resource downloads, site visits)"
              sx={{ mt: 1 }}
            />
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={logOut}
              onChange={() => setLogOut((prev) => !prev)}
              disabled={isLoading}
            />
          }
          label="Log out after reset"
        />
      </FormGroup>

      <Typography
        variant="body1"
        paragraph
        sx={{ mt: 2 }}
        label="ResetUserConfirmationPrompt"
      >
        To proceed, type <strong>{CONFIRMATION_PHRASE}</strong> below.
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        label={'Type "' + CONFIRMATION_PHRASE + '" to confirm'}
        value={confirmationText}
        onChange={(e) => setConfirmationText(e.target.value)}
        sx={{ mb: 2 }}
        disabled={isLoading}
        InputProps={{
          "aria-label": "Confirmation Input",
        }}
      />
      <Button
        variant="contained"
        color="error"
        onClick={handleReset}
        disabled={!canSubmit}
        fullWidth
        aria-label="Reset User Button"
        startIcon={
          isLoading ? <CircularProgress size={20} color="inherit" /> : null
        }
      >
        {isLoading
          ? "Resetting..."
          : resetEverything
            ? "Reset Everything"
            : "Reset Selected Data"}
      </Button>
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Reset completed successfully.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          An error occurred: {error.message}
        </Alert>
      )}
    </Paper>
  );
};
